import { Link as RouterLink, useLocation } from "react-router-dom";
import InstagramIcon from "@mui/icons-material/Instagram";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import FacebookIcon from "@mui/icons-material/Facebook";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { Link } from "@mui/material";
import { CardContent, Tooltip, Typography } from "@avenue-8/sidekick-ui";
import { BulletSeparator } from "@shared/components/BulletSeparator";
import type { SocialProfileSuggestionWithContactResponseDto } from "@shared/generated/crm";
import { deriveContactName } from "@features/Contacts/contacts-helpers";
import {
  PROFILE_REVIEW_PATH,
  type ContactBriefingLocationState,
} from "@shared/utils/contact-briefing-links";
import {
  CONFIDENCE_LABELS,
  MATCHED_FACT_LABELS,
  PLATFORM_LABELS,
  getConfidenceLevel,
  toSafeExternalUrl,
} from "./profile-suggestion-helpers";
import {
  Actions,
  AmbiguityNote,
  CompactButton,
  ConfidenceChip,
  ContactRow,
  FactsRow,
  Header,
  Identity,
  MatchedFactChip,
  MismatchedFactChip,
  PlatformBadge,
  ProfileUrl,
  SuggestionCard,
} from "./ProfileSuggestionCard.styles";

const PLATFORM_ICONS: Record<
  SocialProfileSuggestionWithContactResponseDto["platform"],
  typeof InstagramIcon
> = {
  instagram: InstagramIcon,
  linkedin: LinkedInIcon,
  facebook: FacebookIcon,
};

type ProfileSuggestionCardProps = {
  suggestion: SocialProfileSuggestionWithContactResponseDto;
  onAccept: () => void;
  onReject: () => void;
};

export const ProfileSuggestionCard = ({
  suggestion,
  onAccept,
  onReject,
}: ProfileSuggestionCardProps) => {
  const location = useLocation();
  const contactLinkState: ContactBriefingLocationState = {
    backTo: `${PROFILE_REVIEW_PATH}${location.search}`,
  };
  const { contact, platform, profileName, matchedFacts, nameAmbiguity } =
    suggestion;
  const contactName = deriveContactName(contact) || "this contact";
  const platformLabel = PLATFORM_LABELS[platform];
  const PlatformIcon = PLATFORM_ICONS[platform];
  const safeUrl = toSafeExternalUrl(suggestion.url);
  const confidenceLevel = getConfidenceLevel(suggestion.score);
  const confidencePercentage = Math.round(suggestion.score * 100);
  const matchingFacts = matchedFacts.filter((fact) => fact.matchesContact);
  const nonMatchingFacts = matchedFacts.filter((fact) => !fact.matchesContact);
  const contactDetails = [contact.title, contact.company]
    .filter(Boolean)
    .join(" @ ");

  return (
    <SuggestionCard>
      <CardContent>
        <ContactRow>
          <Typography variant="body2">Possible {platformLabel} for</Typography>
          <Link
            component={RouterLink}
            to={`/contacts/${contact.id}`}
            state={contactLinkState}
            variant="body2"
          >
            {contactName}
          </Link>
          {contactDetails && (
            <>
              <BulletSeparator spacing="0" />
              <Typography variant="body2">{contactDetails}</Typography>
            </>
          )}
        </ContactRow>
        <Header>
          <PlatformBadge>
            <PlatformIcon fontSize="small" />
          </PlatformBadge>
          <Identity>
            <Typography variant="subtitle2">{profileName}</Typography>
            {safeUrl ? (
              <Link
                href={safeUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
              >
                View {platformLabel} profile
              </Link>
            ) : (
              <ProfileUrl variant="body2">{suggestion.url}</ProfileUrl>
            )}
          </Identity>
          <Tooltip
            title={`${confidencePercentage}% match, based on how closely this profile lines up with ${contactName}'s details`}
          >
            <ConfidenceChip
              size="small"
              $level={confidenceLevel}
              label={
                <>
                  {CONFIDENCE_LABELS[confidenceLevel]}
                  <BulletSeparator />
                  {confidencePercentage}%
                </>
              }
            />
          </Tooltip>
        </Header>
        {matchedFacts.length > 0 && (
          <FactsRow>
            {matchingFacts.map((fact) => (
              <MatchedFactChip
                key={fact.field}
                size="small"
                icon={<CheckIcon />}
                label={MATCHED_FACT_LABELS[fact.field]}
              />
            ))}
            {nonMatchingFacts.map((fact) => (
              <MismatchedFactChip
                key={fact.field}
                size="small"
                icon={<CloseIcon />}
                label={`${MATCHED_FACT_LABELS[fact.field]}: ${fact.foundValue}`}
              />
            ))}
          </FactsRow>
        )}
        {nameAmbiguity !== "unique" && (
          <AmbiguityNote variant="body2">
            {nameAmbiguity === "very_common"
              ? `"${profileName}" is a common name, so please make sure this is really ${contactName} before accepting.`
              : "A few other people share similar details, so double-check before accepting."}
          </AmbiguityNote>
        )}
        <Actions>
          <CompactButton
            variant="text"
            size="small"
            onClick={onReject}
            aria-label={`Reject ${platformLabel} profile for ${contactName}`}
          >
            Not a Match
          </CompactButton>
          <CompactButton
            size="small"
            startIcon={<CheckIcon fontSize="small" />}
            onClick={onAccept}
            aria-label={`Accept ${platformLabel} profile for ${contactName}`}
          >
            Confirm Match
          </CompactButton>
        </Actions>
      </CardContent>
    </SuggestionCard>
  );
};
